import { Button, Popover, Spin, Tooltip, message } from 'antd';
import { BarChartOutlined, CheckCircleOutlined, ClockCircleOutlined, SearchOutlined, StarFilled, TagOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import './GoogleReviews.css';
import '../../pages.scss';
import { ButtonLZ } from '../../../components/design/Buttons/ButtonLZ';
import { useFetch } from '../../../../hooks/useFetch/useFetch';
import { useAuth } from '../../../../store/AuthContext/AuthContext';
import moment from 'moment';
import TableLZM from '../../../components/design/table/TableLZM';
import { authFetch } from '../../../../common/request/authFetch';
import { AddLabelModal } from './Reviews/drawer/AddLabelModal';
import { CSVDownloadServer } from '../../../../common/utils/CSVDownloadServer';
import { Label } from './Reviews/Label';
import { EmptyStateBox } from '../../../components/design/EmptyStateBox/EmptyStateBox';
import { LabeledDrawer } from './Reviews/drawer/LabeledDrawer';
import { TableFilter } from '../../../../common/utils/FilterUtils';
import { ResultsPagination } from '../../../components/design/ResultsPagination/ResultsPagination';

const COMMENT_PREVIEW_LENGTH = 140;

const ExpandableComment = ({ text }: { text: string }) => {
	const [expanded, setExpanded] = useState(false);
	const isLong = text.length > COMMENT_PREVIEW_LENGTH;
	const displayText = !text
		? null
		: expanded || !isLong
			? text
			: `${text.slice(0, COMMENT_PREVIEW_LENGTH)}…`;

	return (
		<>
			<p style={{
				color: '#475569',
				fontSize: 12,
				lineHeight: 1.45,
				margin: 0,
				whiteSpace: 'pre-wrap',
			}}>
				{displayText || <i style={{ color: '#94a3b8' }}>Sin texto</i>}
			</p>
			{isLong && (
				<button
					type="button"
					onClick={() => setExpanded((prev) => !prev)}
					style={{
						marginTop: 4,
						padding: 0,
						border: 'none',
						background: 'transparent',
						color: '#2f6fb5',
						fontSize: 12,
						fontWeight: 500,
						cursor: 'pointer',
					}}
				>
					{expanded ? 'Ver menos' : 'Ver más'}
				</button>
			)}
		</>
	);
};

const ManualLabelActions = (props: {
	categories: any[];
	onSelect: (categoryName: string) => void;
	disabled?: boolean;
	disabledReason?: string;
}) => {
	const [open, setOpen] = useState(false);

	if (props.disabled) {
		const reason = props.disabledReason
			|| 'Debes seleccionar un grupo de Google en la cabecera para poder añadir etiquetas manualmente';
		return (
			<Tooltip
				color='transparent'
				overlayInnerStyle={{ padding: 0, boxShadow: 'none' }}
				title={
					<span style={{
						display: 'inline-block',
						padding: '10px 14px',
						borderRadius: 10,
						background: '#fffbeb',
						border: '1px solid #fcd34d',
						color: '#b45309',
						fontSize: 13,
						fontWeight: 500,
						lineHeight: 1.5,
						maxWidth: 280,
					}}>
						{reason}
					</span>
				}
			>
				<span style={{
					display: 'inline-block',
					cursor: 'not-allowed',
					color: '#2f6fb5',
					fontSize: 12,
					fontWeight: 500,
					padding: '2px 0',
				}}>
					+ Añadir etiqueta
				</span>
			</Tooltip>
		);
	}

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			trigger='hover'
			placement='bottomLeft'
			mouseEnterDelay={0.15}
			getPopupContainer={() => document.body}
			content={
				<div style={{ maxWidth: 280 }}>
					<div style={{ fontSize: 11, color: 'slategrey', marginBottom: 6, fontWeight: 500 }}>
						Elige una categoría para tu etiqueta:
					</div>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
						{props.categories?.length
							? props.categories.map((category: any) => (
								<ButtonLZ
									key={category.name}
									size='small'
									type='link'
									onClick={() => {
										setOpen(false);
										props.onSelect(category.name);
									}}
								>
									<small>{category.name}</small>
								</ButtonLZ>
							))
							: <small style={{ color: 'grey' }}>No hay categorías configuradas</small>
						}
					</div>
				</div>
			}
		>
			<span style={{
				display: 'inline-block',
				cursor: 'pointer',
				color: '#2f6fb5',
				fontSize: 12,
				fontWeight: 500,
				padding: '2px 0',
			}}>
				+ Añadir etiqueta
			</span>
		</Popover>
	);
};

export const CommentsList = (props: {filterCategory: string, filterSentiment: string, filterLabeled: string, filterPeriod: string, categories: any}) => {

	const [updateData, setUpdateData] = useState(false);
	const [reviews, setReviews] = useState([]);
    const [reviewIdLoading, setReviewIdLoading] = useState<string>();	
    const [addLabelModal, setAddLabelModal] = useState<any>(null);
	const { projectGroup, getGroupName } = useAuth();
    const [pagination, setPagination] = useState<any>({page:1});  
    const [filterPage, setFilterPage] = useState('');	
    const [labeledDrawer, setLabeledDrawer] = useState(false);

    useEffect(() => {            
        if (filterPage !== '') {
            setFilterPage('&page=1');					
        }
    }, [projectGroup, props.filterCategory, props.filterSentiment, props.filterLabeled, props.filterPeriod])


	const { isLoading } = useFetch(`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/comments/list?${props.filterCategory}${props.filterSentiment}${props.filterLabeled}${props.filterPeriod}${filterPage}`
	  	, {},[updateData]
		,(data: any) => {      			
            setPagination(data.pagination);
			setReviews(data.reviews);		
		}
        );
        
    const labelReview = (reviewId: string) => {
        
        setReviewIdLoading(reviewId);
        const data = {
            reviewId: reviewId
        };
        
        authFetch(`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/comments/`, { method: 'POST', data })
        .then((ok: boolean) => {	        
            if (ok) {
                setUpdateData(!updateData)
            } else {
                message.error("Error al etiquetar", 3);
            }
            setReviewIdLoading('');
        })                
    }    

    const esTodosLosGrupos = getGroupName() === 'Todos los Grupos';

	const columns: any = [     
        {
            title: 'Reseña',
            key: 'text',
            width: 320,
            render: (_: string, record: any) => {
                const commentText = record.comment?.originalText || '';

                return (
                    <div style={{ padding: '8px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            <img
                                src={record.reviewerProfilePhotoUrl}
                                alt=""
                                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 13 }}>
                                        {record.reviewerDisplayName}
                                    </span>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 3,
                                        color: '#94a3b8',
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}>
                                        <StarFilled style={{ fontSize: 12, color: '#cbd5e1' }} />
                                        {(Math.round(10 * record.starRating) / 10).toFixed(1)}
                                    </span>
                                </div>
                                <Tooltip title={<>
                                    <div>Creada el {moment(record.createdTime).format('ll')}</div>
                                    {!moment(record.createdTime).isSame(moment(record.updateTime)) && (
                                        <div>Actualizada el {moment(record.updateTime).format('ll')}</div>
                                    )}
                                </>}>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                                        {moment(record.updateTime).fromNow()}
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                        <div style={{
                            background: '#f8fafc',
                            borderRadius: 8,
                            padding: '8px 12px',
                            borderLeft: '3px solid #e2e8f0',
                        }}>
                            <ExpandableComment text={commentText} />
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Etiquetas',
            key: 'label',
            width: 400,
            render: (_: string, record: any) => {
                const labeled = !!record.labeled;
                const hasLabels = record.commentLabels?.length > 0;
                const hasManualLabel = record.commentLabels?.some((label: any) => !!label.manual);
                const labelScope = labeled
                    ? (hasManualLabel ? 'Manual' : 'Automático')
                    : null;

                return (
                    <div style={{ padding: '6px 0' }}>
                        <div style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: labeled
                                ? '1px solid rgba(56, 158, 13, 0.22)'
                                : '1px solid rgba(196, 29, 127, 0.22)',
                            background: labeled ? 'rgba(240, 253, 244, 0.45)' : 'rgba(255, 241, 242, 0.45)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                color: labeled ? '#389e0d' : '#c41d7f',
                                fontSize: 11,
                                fontWeight: 600,
                            }}>
                                {labeled
                                    ? <CheckCircleOutlined style={{ fontSize: 12 }} />
                                    : <ClockCircleOutlined style={{ fontSize: 12 }} />
                                }
                                {labeled ? 'Etiquetado' : 'Pendiente'}
                                {labelScope && (
                                    <span style={{
                                        background: '#f1f3fa',
                                        color: 'slategrey',
                                        fontWeight: 500,
                                        fontSize: 11,
                                        borderRadius: 8,
                                        padding: '1.5px 8px',
                                        border: '1px solid #dbe2f5',
                                    }}>
                                        {labelScope}
                                    </span>
                                )}
                            </div>

                            {labeled && !hasLabels && (
                                <div style={{ color: '#64748b', fontSize: 12, fontWeight: 500 }}>
                                    <span style={{ color: '#b0b8c1' }}>
                                        Este comentario no tiene etiquetas asociadas
                                    </span>
                               
                                </div>
                            )}

                            {hasLabels && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                                    {record.commentLabels.map((label: any) => (
                                        <Label
                                            key={`${record.name}-${label.category}-${label.label}`}
                                            reviewId={record.name}
                                            label={label}
                                            showCategory
                                            onChange={() => setUpdateData(!updateData)}
                                        />
                                    ))}
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <ManualLabelActions
                                    categories={props.categories}
                                    disabled={esTodosLosGrupos}
                                    disabledReason="Debes seleccionar un grupo de Google en la cabecera para poder añadir etiquetas manualmente"
                                    onSelect={(categoryName) => {
                                        setAddLabelModal({
                                            reviewId: record.name,
                                            category: categoryName,
                                            score: record.starRating,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                );
            }
        },        
        {
            title: 'Local',
            key: 'locality',
            width: 240,
            filterDropdown: TableFilter,
            filterIcon: () => <SearchOutlined />,
            onFilter: (value: any, record: any) => {
                const query = value?.toLowerCase();
                return record.title?.toLowerCase().includes(query)
                    || record.addressLine?.toLowerCase().includes(query)
                    || record.locality?.toLowerCase().includes(query)
                    || record.administrativeArea?.toLowerCase().includes(query);
            },
            render: (_: string, record: any) => (
                <div style={{
                    background: 'transparent',
                    borderRadius: 10,
                    maxWidth: 280,
                    border: '1px solid rgba(148, 163, 184, 0.28)',
                    padding: '10px 12px',
                }}>
                    <div style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#0f172a',
                        lineHeight: 1.3,
                        marginBottom: 4,
                    }}>
                        {record.title}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.35 }}>
                        {record.addressLine
                            ? `${record.addressLine}, ${record.locality}, ${record.administrativeArea}`
                            : 'Sin ubicación'}
                    </div>
                </div>
            )
        },     
        {
            title: '',
            key: 'IA',
            width: 150,
            align: 'center',            
            render: (_: string, record: any) => (
                <ButtonLZ
                    size='small'
                    type='ghost'
                    onClick={() => { labelReview(record.name); }}
                    style={{
                        fontSize: 12,
                        border: '1px solid #e9d5ff',
                        background: '#faf5ff',
                        color: '#7e22ce',
                        height: 30,
                        padding: '0 10px',
                    }}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
                        {reviewIdLoading !== record.name
                            ? <span className="material-symbols-outlined" style={{ fontSize: 15, color: '#7e22ce' }}>auto_awesome</span>
                            : <Spin size='small' />
                        }
                        Etiquetar IA
                    </span>
                </ButtonLZ>
			)			
        }     
    ]

	return (
		<>				
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', color: 'slategrey', marginBottom: 4}}>
                {reviews && pagination && 
                    <ResultsPagination
                        pagination={pagination}
                        pageItemsCount={reviews.length}
                        align='start'
                        disabled={isLoading}
                        onPrev={() => { setFilterPage('&page=' + (pagination.page - 1)); }}
                        onNext={() => { setFilterPage('&page=' + (pagination.page + 1)); }}
                    />
                }
                <ButtonLZ 
                    type='ghost' 
                    onClick={() => setLabeledDrawer(true)} 
                    size='small'
                    style={{ 
                        borderRadius: '4px', 
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        height: '24px',
                        marginLeft: 'auto',
                    }}
                >
                    <BarChartOutlined /> Etiquetado vs Pendiente
                </ButtonLZ>
            </div>
            <Spin spinning={isLoading}>
                {!reviews?.length && !isLoading ? (
                    <div style={{ margin: '12px 0' }}>
                        <EmptyStateBox
                            icon={<TagOutlined />}
                            title="No hay reseñas"
                            description="No hay reseñas con comentarios para los filtros seleccionados."
                        />
                    </div>
                ) : (
                    <TableLZM size='small'                
                        pagination={false} 
                        loading={isLoading}                
                        rowKey="name"
                        columns={columns} 
                        dataSource={reviews}                    
                        scroll={{ y: 600 }}
                        />
                )}
            </Spin>

            {!!reviews?.length && <CSVDownloadServer url={`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/comments/listCSV?${props.filterCategory}${props.filterSentiment}${props.filterLabeled}${props.filterPeriod}${filterPage}`} fileName='Lista de Comentarios.csv'/>}
            { addLabelModal && <AddLabelModal reviewId={addLabelModal.reviewId} score={addLabelModal.score} category={addLabelModal.category} onFinish={() => {setAddLabelModal(null); setUpdateData(!updateData)}}/> }                            
            {labeledDrawer && <LabeledDrawer onFinish={() => {setLabeledDrawer(false);}} />}										
		</>
	)
}
